import React, { useEffect, useRef, useState } from "react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { toast } from "sonner";
import api from "@/lib/api";
import { Upload, X, Image as ImageIcon, Maximize2, Crop } from "lucide-react";

const TARGET_W = 1200;
const TARGET_H = 800; // 3:2
const ASPECT = TARGET_W / TARGET_H;

/**
 * ThumbnailCropper — opens a modal that lets the user:
 *   1) Pick an image file
 *   2) Visually crop it to 3:2 (drag corners / move the box), OR
 *   3) "Fit whole image" — pads with a chosen background colour so NOTHING is chopped off.
 * Uploads the rendered result with ?as=thumbnail&pre_cropped=1 so the backend
 * stores it as-is (no second auto-crop).
 *
 * Props:
 *   currentUrl   — string, existing thumbnail (for status display only)
 *   onUploaded   — ({ url, filename, size, content_type }) => void
 *   testid       — string, root testid prefix
 */
export default function ThumbnailCropper({ currentUrl, onUploaded, testid = "thumb-cropper" }) {
  const fileInputRef = useRef(null);
  const imgRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [srcUrl, setSrcUrl] = useState(null);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [mode, setMode] = useState("crop"); // "crop" | "fit"
  const [bg, setBg] = useState("#ffffff");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [natural, setNatural] = useState({ w: 0, h: 0 });

  const pickFile = () => fileInputRef.current?.click();

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    const url = URL.createObjectURL(f);
    setSrcUrl(url);
    setMode("crop");
    setBg("#ffffff");
    setCrop(undefined);
    setCompletedCrop(null);
    setOpen(true);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onImageLoad = (e) => {
    const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
    setNatural({ w, h });
    // Auto-init crop: largest 3:2 box that fits, centered.
    const initial = centerCrop(
      makeAspectCrop({ unit: "%", width: 90 }, ASPECT, w, h),
      w,
      h,
    );
    setCrop(initial);
  };

  // Render output to a canvas based on chosen mode
  const renderBlob = async () => {
    const canvas = document.createElement("canvas");
    canvas.width = TARGET_W;
    canvas.height = TARGET_H;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas not supported");

    const img = imgRef.current;
    if (!img) throw new Error("Image not ready");

    if (mode === "crop") {
      if (!completedCrop || completedCrop.width === 0 || completedCrop.height === 0) {
        throw new Error("Drag the crop box first");
      }
      // completedCrop is in displayed-image pixels — convert to natural pixels.
      const scaleX = img.naturalWidth / img.width;
      const scaleY = img.naturalHeight / img.height;
      const sx = completedCrop.x * scaleX;
      const sy = completedCrop.y * scaleY;
      const sw = completedCrop.width * scaleX;
      const sh = completedCrop.height * scaleY;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, TARGET_W, TARGET_H);
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, TARGET_W, TARGET_H);
    } else {
      // Fit whole image, pad with chosen background
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, TARGET_W, TARGET_H);
      const iw = img.naturalWidth;
      const ih = img.naturalHeight;
      const scale = Math.min(TARGET_W / iw, TARGET_H / ih);
      const dw = iw * scale;
      const dh = ih * scale;
      const dx = (TARGET_W - dw) / 2;
      const dy = (TARGET_H - dh) / 2;
      ctx.drawImage(img, 0, 0, iw, ih, dx, dy, dw, dh);
    }

    return await new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/jpeg", 0.9));
  };

  const doUpload = async () => {
    try {
      setUploading(true);
      setProgress(0);
      const blob = await renderBlob();
      if (!blob) throw new Error("Could not render image");
      const form = new FormData();
      form.append("file", new File([blob], "thumbnail.jpg", { type: "image/jpeg" }));
      const r = await api.post("/upload", form, {
        params: { as: "thumbnail", pre_cropped: 1 },
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 5 * 60 * 1000,
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round(100 * evt.loaded / evt.total));
        },
      });
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const fullUrl = `${backendUrl}${r.data.url}`;
      onUploaded?.({ ...r.data, url: fullUrl });
      toast.success("Thumbnail saved");
      closeModal();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const closeModal = () => {
    setOpen(false);
    if (srcUrl) URL.revokeObjectURL(srcUrl);
    setSrcUrl(null);
    setCrop(undefined);
    setCompletedCrop(null);
  };

  useEffect(() => () => { if (srcUrl) URL.revokeObjectURL(srcUrl); }, [srcUrl]);

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`${testid}-wrap`}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={onFileChange}
        className="hidden"
        data-testid={`${testid}-input`}
      />
      <button
        type="button"
        onClick={pickFile}
        className="btn-secondary text-xs"
        data-testid={`${testid}-open`}
      >
        <Upload size={12} strokeWidth={1.5} /> {currentUrl ? "Replace thumbnail" : "Choose & crop thumbnail"}
      </button>
      {currentUrl && (
        <span className="overline text-[#166534]" data-testid={`${testid}-status`}>image saved</span>
      )}

      {open && (
        <div
          className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center px-4 py-8"
          data-testid={`${testid}-modal`}
        >
          <div className="bg-white max-w-4xl w-full border border-[#0A0A0A] flex flex-col max-h-[92vh]">
            <div className="px-6 py-4 border-b border-[#E4E4E7] flex items-center justify-between">
              <div>
                <div className="overline text-[#E63946]">Thumbnail</div>
                <h2 className="font-serif text-2xl">Crop or fit your image</h2>
                <p className="text-xs text-[#52525B] mt-1">
                  Course thumbnails are 3:2. Drag the box to crop, or switch to <em>Fit whole image</em> to keep every pixel (no text gets chopped).
                </p>
              </div>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-[#F9F9F8]"
                data-testid={`${testid}-close`}
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>

            <div className="px-6 py-3 border-b border-[#E4E4E7] flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setMode("crop")}
                className={`text-xs px-3 py-2 border ${mode === "crop" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E4E4E7] hover:bg-[#F9F9F8]"}`}
                data-testid={`${testid}-mode-crop`}
              >
                <Crop size={12} className="inline mr-1" /> Crop to 3:2
              </button>
              <button
                type="button"
                onClick={() => setMode("fit")}
                className={`text-xs px-3 py-2 border ${mode === "fit" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E4E4E7] hover:bg-[#F9F9F8]"}`}
                data-testid={`${testid}-mode-fit`}
              >
                <Maximize2 size={12} className="inline mr-1" /> Fit whole image (no chop)
              </button>
              {mode === "fit" && (
                <div className="flex items-center gap-2 ml-auto">
                  <span className="overline text-[#52525B]">Background</span>
                  <input
                    type="color"
                    value={bg}
                    onChange={(e) => setBg(e.target.value)}
                    className="h-8 w-12 border border-[#E4E4E7] cursor-pointer"
                    data-testid={`${testid}-bg-color`}
                  />
                  <div className="flex gap-1">
                    {["#ffffff", "#0A0A0A", "#F9F9F8", "#E63946", "#1E40AF"].map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setBg(c)}
                        className="w-6 h-6 border border-[#E4E4E7]"
                        style={{ background: c }}
                        title={c}
                        data-testid={`${testid}-bg-swatch-${c}`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex-1 overflow-auto bg-[#F4F4F5] p-6 flex items-center justify-center">
              {srcUrl && mode === "crop" && (
                <ReactCrop
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={ASPECT}
                  keepSelection
                >
                  <img
                    ref={imgRef}
                    src={srcUrl}
                    onLoad={onImageLoad}
                    alt="to crop"
                    style={{ maxHeight: "60vh", maxWidth: "100%" }}
                    data-testid={`${testid}-image`}
                  />
                </ReactCrop>
              )}
              {srcUrl && mode === "fit" && (
                <FitPreview src={srcUrl} bg={bg} imgRef={imgRef} onLoaded={(w, h) => setNatural({ w, h })} testid={testid} />
              )}
            </div>

            <div className="px-6 py-4 border-t border-[#E4E4E7] flex items-center justify-between flex-wrap gap-3">
              <div className="text-xs text-[#52525B] flex items-center gap-2">
                <ImageIcon size={12} />
                {natural.w > 0 && (
                  <span>Source {natural.w}×{natural.h} → Saved as 1200×800 JPEG</span>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn-secondary text-xs"
                  data-testid={`${testid}-cancel`}
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={doUpload}
                  disabled={uploading || !srcUrl}
                  className="btn-primary text-xs"
                  data-testid={`${testid}-save`}
                >
                  {uploading ? `Saving… ${progress}%` : "Save thumbnail"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FitPreview({ src, bg, imgRef, onLoaded, testid }) {
  // Visual 3:2 box with the image fully fitted inside — what the user will get.
  const ratio = TARGET_W / TARGET_H;
  return (
    <div
      style={{
        background: bg,
        width: "min(720px, 100%)",
        aspectRatio: `${ratio}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: "1px solid #E4E4E7",
      }}
      data-testid={`${testid}-fit-preview`}
    >
      <img
        ref={imgRef}
        src={src}
        onLoad={(e) => onLoaded(e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)}
        alt="fit preview"
        style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
        data-testid={`${testid}-fit-image`}
      />
    </div>
  );
}
